import type { ApiClient } from '@classgo/app-kit';
import type {
  AnnouncementsQuery,
  AnnouncementView,
  AttachmentView,
  CreateAnnouncementInput,
  FeedItem,
  PageOf,
  RecipientView,
  RegisterAttachmentInput,
  UpdateAnnouncementInput,
} from '@classgo/shared';
import { queryOptions } from '@tanstack/react-query';

const qs = (query: Record<string, string | number | undefined>) => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
};

/**
 * The announcements endpoints (ticket #66): composer, publication, recipients, attachments,
 * the recipient's feed and receipts.
 *
 * @param {ApiClient} api -- The client.
 * @returns The calls.
 */
export const announcementsApi = (api: ApiClient) => ({
  create: (body: CreateAnnouncementInput) =>
    api.request<AnnouncementView>('/announcements', { method: 'POST', body }),
  update: (id: string, body: UpdateAnnouncementInput) =>
    api.request<AnnouncementView>(`/announcements/${id}`, { method: 'PATCH', body }),
  publish: (id: string) =>
    api.request<AnnouncementView>(`/announcements/${id}/publish`, { method: 'POST' }),
  archive: (id: string) =>
    api.request<{ ok: boolean }>(`/announcements/${id}`, { method: 'DELETE' }),
  registerAttachment: (id: string, body: RegisterAttachmentInput) =>
    api.request<AttachmentView>(`/announcements/${id}/attachments`, { method: 'POST', body }),
  attachmentLink: (id: string, attachmentId: string) =>
    api.request<{ url: string; filename: string }>(
      `/announcements/${id}/attachments/${attachmentId}`,
    ),
  markRead: (id: string) =>
    api.request<{ readAt: string }>(`/announcements/${id}/read`, { method: 'POST' }),
  presignAttachment: (filename: string, contentType: string, sizeBytes: number) =>
    api.request<{ key: string; url: string; expiresAt: string }>('/uploads/presign', {
      method: 'POST',
      body: { purpose: 'attachment', filename, contentType, sizeBytes },
    }),
});

/**
 * Query options of the announcement screens.
 */
export const announcementQueries = {
  list: (api: ApiClient, query: Partial<AnnouncementsQuery>) =>
    queryOptions({
      queryKey: ['announcements', 'list', query],
      queryFn: () =>
        api.request<PageOf<AnnouncementView>>(`/announcements${qs({ pageSize: 20, ...query })}`),
    }),
  detail: (api: ApiClient, id: string) =>
    queryOptions({
      queryKey: ['announcements', 'detail', id],
      queryFn: () => api.request<AnnouncementView>(`/announcements/${id}`),
      enabled: id !== '',
    }),
  recipients: (api: ApiClient, id: string) =>
    queryOptions({
      queryKey: ['announcements', 'recipients', id],
      queryFn: () => api.request<{ items: RecipientView[] }>(`/announcements/${id}/recipients`),
      enabled: id !== '',
    }),
  feed: (api: ApiClient) =>
    queryOptions({
      queryKey: ['me', 'announcements'],
      queryFn: () => api.request<{ items: FeedItem[] }>('/me/announcements'),
    }),
};
